import React from 'react';
import { Box, Stack, Chip, Paper, Typography, Grid } from '@mui/material';
import { format } from 'date-fns';
import { Event } from '@/types/events';

interface ViewModeProps {
  event: Event;
}

const DetailBox = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Paper
    sx={{
      p: 2,
      borderRadius: 1,
      bgcolor: '#f8f8f8',
      border: '1px solid #e0e0e0',
      mb: 1.5,
      height: '100%'
    }}
  >
    <Typography variant="subtitle2" gutterBottom>
      {title}
    </Typography>
    {children}
  </Paper>
);

export const ViewMode: React.FC<ViewModeProps> = ({ event }) => {
  return (
    <Box sx={{ 
      typography: { fontSize: 14, lineHeight: 1.5 },
      maxWidth: 1000,
      mx: 'auto'
    }}>
      <Typography variant="h5" sx={{ mb: 2.5, fontWeight: 600 }}>
        {event.title}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <DetailBox title="Description">
            <Typography>{event.description}</Typography>
          </DetailBox>
        </Grid>

        <Grid item xs={12} md={6}>
          <DetailBox title="Date & Time">
            <Stack spacing={1}>
              <Typography>
                Start: {format(new Date(event.startDate), 'PPP p')}
              </Typography>
              <Typography>
                End: {format(new Date(event.endDate), 'PPP p')}
              </Typography>
            </Stack>
          </DetailBox>
        </Grid>

        <Grid item xs={12} md={6}>
          <DetailBox title="Location">
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip 
                  label={event.locationType} 
                  size="small" 
                  color="primary" 
                />
                <Typography>{event.location}</Typography>
              </Stack>
              {event.virtualLink && (
                <Typography sx={{ wordBreak: 'break-all' }}>
                  Virtual Link: {event.virtualLink}
                </Typography>
              )}
            </Stack>
          </DetailBox>
        </Grid>

        <Grid item xs={12} md={6}>
          <DetailBox title="Target Grades">
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {event.targetGrades.map((grade) => (
                <Chip 
                  key={grade} 
                  label={`Grade ${grade}`} 
                  size="small" 
                  variant="outlined" 
                />
              ))}
            </Stack>
          </DetailBox>
        </Grid>

        <Grid item xs={12} md={6}>
          <DetailBox title="Objectives">
            <Stack spacing={1}>
              {event.objectives.map((objective, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    display: 'flex',
                    '&::before': {
                      content: '"•"',
                      mr: 1,
                      color: 'primary.main'
                    }
                  }}
                >
                  <Typography>{objective}</Typography>
                </Box>
              ))}
            </Stack>
          </DetailBox>
        </Grid>
      </Grid>
    </Box>
  );
};